import { useRef } from "react";
import cx from "classnames";
import { faPrescription } from "@fortawesome/pro-regular-svg-icons";

import { toDollars } from "common/utils/formatting";
import LinkButton from "common/components/LinkButton";
import CatalogImage from "common/components/CatalogImage";
import Icon from "common/components/Icon";

import TextChip from "client/common/components/content/TextChip";
import { urls } from "client/routes";
import { ProductListSource } from "client/analytics/types";

import { ProductSearchResult } from "../types";
import { useProductSearchCardFields } from "../hooks/utils";
import ProductSearchAutoshipPricing from "./ProductSearchAutoshipPricing";
import ProductSearchDiscountBadges from "./ProductSearchDiscountBadges";

import styles from "../styles/ProductCard.module.less";

type ProductCardProps = {
  result: ProductSearchResult;
  listSource: ProductListSource;
  collectionSlug?: string;
};

const ProductCard = ({ result, listSource, collectionSlug }: ProductCardProps) => {
  const resultRef = useRef<HTMLDivElement>(null);
  const {
    productLine,
    hasPrescriptionLabel,
    isFeatured,
    variantLabel,
    packLabel,
    optionsLabel,
    facts,
    handleClick,
  } = useProductSearchCardFields(result, listSource, collectionSlug);
  const hasSalePrice = result.default_item.standard_list_price > result.default_item.standard_price;

  return (
    <LinkButton
      dest={urls.products.detail({
        params: { id: productLine.id },
        search: { item: result.default_item.id, product: result.id },
      })}
      className={styles.container}
      onClick={handleClick}
    >
      <div className={styles.content} ref={resultRef}>
        {/* Rx mark (top-right). Compounds are flagged by a "Compounded" fact chip, not the icon. */}
        {productLine.prescription_required && (
          <span className={styles.prescription}>
            <Icon fixedWidth={true} icon={faPrescription} />
          </span>
        )}
        <div
          className={cx(
            styles.background,
            hasPrescriptionLabel && styles.prescribed,
            !hasPrescriptionLabel && isFeatured && styles.featured,
          )}
        >
          {(hasPrescriptionLabel || isFeatured) &&
            (hasPrescriptionLabel ? (
              <TextChip variant="info" className={styles.chip}>
                {result.patient_prescription_label}
              </TextChip>
            ) : (
              <TextChip variant="highlight" className={styles.chip}>
                Your Vet&apos;s Choice
              </TextChip>
            ))}
          <CatalogImage
            image={result.main_image}
            size="medium"
            alt={result.name}
            className={styles.image}
          />
        </div>
        <ProductSearchDiscountBadges discounts={result.default_item.discounts} styles={styles} />
      </div>
      <div className={styles.info}>
        <div className={styles.name}>{productLine.name}</div>
        <div className={styles.variant}>{variantLabel && <span>{variantLabel}</span>}</div>
        {/* Reserving the title/desc/breakdown rows locks cards to a consistent height so the
            price and chips line up across a row. */}
        <div className={styles.desc}>{productLine.client_description}</div>
        <div className={styles.breakdown}>
          {packLabel && <span className={styles.pack}>{packLabel}</span>}
          {optionsLabel && <span className={styles.options}>+{optionsLabel}</span>}
        </div>
        <div className={styles.meta}>
          {hasSalePrice && (
            <span className={styles.priceWas}>
              {toDollars(result.default_item.standard_list_price)}
            </span>
          )}
          <span className={styles.price}>{toDollars(result.default_item.standard_price)}</span>
          <ProductSearchAutoshipPricing defaultItem={result.default_item} styles={styles} />
        </div>
        <div className={styles.footer}>
          {facts.length > 0 && (
            <div className={styles.facts}>
              {facts.map((fact) => (
                <TextChip key={`${fact.label}-${fact.value}`} variant="grey" size="s">
                  {fact.value}
                </TextChip>
              ))}
            </div>
          )}
        </div>
      </div>
    </LinkButton>
  );
};

export default ProductCard;
